import { z } from 'zod'
import { Block, CompartimentId, RONDE_PATROON } from './id.js'
import { dossierContractProblems } from './kolomtype.js'
import { Text } from './text.js'

/**
 * Een contract is de beschreven vorm van het JSON-document dat een bouwblok op
 * het dossier achterlaat (D-37, ontwerp 9.5): een JSON Schema-document, met per
 * veld een naam, een type en een betekenis.
 *
 * De registry is die verzameling plus een index met de ketenvolgorde, en wordt
 * aan de configurator aangeleverd. De configurator leest nooit zelf in Dataverse
 * (U26, B10); dat is wat U14 overeind houdt, want dan is de kennis van
 * bouwblokken een gegeven en geen koppeling.
 *
 * Dit schema is met opzet niet strikt. Een contract is een echt
 * JSON Schema-document en mag alle trefwoorden dragen die de standaard kent; wat
 * hier wordt getoetst is of het genoeg beschrijft om een veld uit te kiezen en
 * bij publicatie te controleren (D-37, D-40). Alles afwijzen wat wij niet kennen
 * zou geldige contracten weigeren.
 *
 * **Niet strikt is niet hetzelfde als vormvrij.** Dit product schrijft het formaat van een
 * contract voor (U27, `docs/koppelvlak.md`), en dat gaat verder dan wat JSON Schema zelf
 * levert: twee gereserveerde sleutels op de wortel van een blokdocument (D-69, D-72), een
 * rol per eigenschap van een reeks (D-73) en een type dat een lijst mag zijn (R-148). Wat
 * niet in dat formaat past wordt geweigerd en niet ingepast; wat het formaat niet kent maar
 * de standaard wel, blijft toegestaan.
 */

export const JsonTypeName = z.enum(['object', 'array', 'string', 'number', 'integer', 'boolean', 'null'])
export type JsonTypeName = z.infer<typeof JsonTypeName>

/**
 * Het type van een eigenschap: één naam, of een lijst namen.
 *
 * **Waarom een lijst nodig is, en waar dat vandaan komt.** Het oordeel van een beoordelend
 * bouwblok wordt gegenereerd uit `Oordeel` in `laag.ts`, en daar is `oordeel` een union van
 * een geheel getal en tekst, want de oordeelschaal is instelbaar: een cijferbereik levert een
 * getal, een tweekeuze een tekst (R-148). Eén type kiezen betekent kiezen tussen twee schalen
 * die R-148 allebei toestaat, en `string` voor beide laat het cijfer als tekst reizen en laat
 * elke lezer parsen. Twee eigenschappen naast elkaar geeft één begrip twee namen. Een typelijst
 * is wat JSON Schema hiervoor heeft, en de generator leidt hem af uit de union die er al staat,
 * dus er ontstaat geen tweede definitie (besluit 3 van M7 opdracht 9, ontwerp 9.5, D-37).
 *
 * De lijst is niet leeg en bevat geen naam twee keer: dat eist het formaat zelf, want
 * `["string", "string"]` zegt niets meer dan `"string"` en is in JSON Schema geen geldige
 * `type`-waarde.
 */
const JsonType = z.union([
  JsonTypeName,
  z
    .array(JsonTypeName)
    .min(1)
    .refine((types) => new Set(types).size === types.length, {
      error: 'een typelijst noemt hetzelfde type niet twee keer',
    }),
])

/**
 * De rol van een eigenschap van een reekselement (D-73).
 *
 * Waar een document een reeks draagt, heeft elke eigenschap van een reekselement een rol:
 * `vraag`, `periode` of `antwoord`. Daarmee kan het formulier de antwoorden per vraag onder
 * elkaar zetten met de periode erbij, en dat is wat R-62 vraagt; de weergavemodus met historie
 * (R-137) is dezelfde groepering in een andere gedaante.
 *
 * **Optioneel, en dat is geen slordigheid.** Een eigenschap zonder rol doet niet mee in de
 * groepering en staat wel in het document; `revisieId` is zo'n eigenschap, want die verwijst
 * naar de beoordeelde stand en is geen antwoord op een vraag. En een reeks zonder een
 * eigenschap met `rol: vraag` wordt als platte tabel getoond, zonder waarschuwing: niet elke
 * reeks is een vraag met antwoorden, en een waarschuwing bij een geldig contract leert de
 * lezer waarschuwingen te negeren (besluit 2 van M7 opdracht 9).
 *
 * Geen `x-`-voorvoegsel: dat voorvoegsel bestaat om een uitbreiding op het formaat van iemand
 * anders te markeren, en dit product bezit dit formaat (U27).
 *
 * De waarde van een eigenschap met `rol: periode` is de uitvraagaanduiding van de instantie
 * die de stand uitgaf (`UitvraagRef`) en geen vrije tekst; die aanduiding is de enige die per
 * instantie stabiel is. Het model kan dat hier niet afdwingen, want in het contract staat
 * alleen de vorm van het document en niet de herkomst van de waarde.
 */
/**
 * Wat een tekst is, als het een datum of een tijdstip is, en wat een getal is, als het een bedrag is
 * (`koppelvlak.md` hoofdstuk 2). Optioneel en additief: het type blijft `string` of `number`, en het
 * formaat zegt welk `datatype` het element in het XML draagt (`date`, `datetime` of `amount`). Bij een
 * datum en een tijdstip zegt het ook welk type het XML Schema geeft (`xs:date` of `xs:dateTime`); een
 * bedrag blijft `xs:decimal`. Zonder formaat is een tekst gewoon tekst en een getal gewoon een getal.
 *
 * **Het bedrag kwam erbij op 31 oktober 2026** (R-54, besluit van de gebruiker): een verwijzing naar het
 * budget van de call kwam als `750.000` in beeld, want van een blok buiten dit product weet de pod niet
 * dat een getal een bedrag is. Het formaat heet `amount`, zoals het `datatype` op de lijn en zoals
 * `date` en `date-time` de woorden van de standaard volgen.
 */
export const ContractFormat = z.enum(['date', 'date-time', 'amount'])
export type ContractFormat = z.infer<typeof ContractFormat>

/**
 * De valuta van een bedrag (R-54). Eén waarde, want het product kent er één, zoals het bedragveld
 * (`AmountView` in `sourceView.ts`). Hij staat toch uitgeschreven in het contract, zodat een lezer van
 * buiten hem niet hoeft aan te nemen, en een tweede valuta later een waarde erbij is en geen nieuw
 * formaat.
 */
export const ContractCurrency = z.literal('EUR')
export type ContractCurrency = z.infer<typeof ContractCurrency>

/**
 * Een bedrag is een getal met zijn valuta, en alleen een bedrag heeft een valuta (R-54).
 *
 * Alleen voor het bedrag en niet voor `date` en `date-time`: die stonden er al zonder deze toets, en
 * een contract dat vandaag geldt, blijft gelden.
 */
function amountProblems(node: { readonly type: unknown; readonly format?: unknown; readonly valuta?: unknown }, ctx: z.RefinementCtx): void {
  const types = Array.isArray(node.type) ? node.type : [node.type]
  if (node.format === 'amount') {
    if (types.length !== 1 || (types[0] !== 'number' && types[0] !== 'integer')) {
      ctx.addIssue({ code: 'custom', path: ['type'], message: 'een bedrag is een getal: type number of integer' })
    }
    if (node.valuta === undefined) ctx.addIssue({ code: 'custom', path: ['valuta'], message: 'een bedrag noemt zijn valuta (EUR)' })
  } else if (node.valuta !== undefined) {
    ctx.addIssue({ code: 'custom', path: ['valuta'], message: 'alleen een bedrag heeft een valuta (format amount)' })
  }
}

export const ContractRole = z.enum(['vraag', 'periode', 'antwoord'])
export type ContractRole = z.infer<typeof ContractRole>

/**
 * De twee sleutels die op de wortel van een blokdocument voor het formaat zelf zijn
 * gereserveerd (D-37, D-69, D-72).
 *
 * `compartimenten` houdt de velden die de inrichter zelf heeft toegevoegd, op
 * `compartimenten.<compartiment-id>.<veld-id>` (D-69). `$stand` draagt de aanduiding van de
 * stand die het document is: onze `RevisieId` bij een eigen blok, een stabiele en binnen dat
 * blok unieke aanduiding bij een blok buiten dit product (D-72).
 *
 * Een contract dat een van deze namen als eigenschap gebruikt wordt geweigerd en niet
 * ingepast (U27). Zonder dat verbod kan hetzelfde pad twee dingen betekenen, en dan moet elk
 * koppelend systeem per contract een botsingscontrole bouwen; één verbod hier kost één
 * controle en daarna nooit meer een.
 */
export const RESERVED_KEYS = ['compartimenten', '$stand'] as const

/**
 * Een schema dat op een plaats staat waar het geen naam heeft, zoals de `items`
 * van een array. Hier geldt de betekenisplicht niet: D-37 gaat over een veld met
 * een naam, en de betekenis van een rij staat in de `description` van de array
 * eromheen. Zo valideert het voorbeeldcontract uit ontwerp 9.5 zoals het er staat.
 *
 * **`.meta({ id })`, voor de bijlage die `koppelvlak.md` regel 7 belooft.** Zonder een `id` in de
 * metadata-registry valt een recursief schema in het gegenereerde JSON Schema terug op een
 * verzonnen naam (`__schema7`), onleesbaar voor een externe partij. `.meta()` retourneert een
 * nieuwe schema-instantie in plaats van de bestaande te muteren, dus de `id` moet op de export
 * zelf staan en niet als losse aanroep ernaast; de recursieve verwijzing hieronder (`items:
 * ContractVorm.optional()`) leest dezelfde module-scoped const en ziet daardoor vanzelf de
 * benoemde versie, want de `z.lazy`-callback loopt pas als het schema wordt gebruikt.
 */
export const ContractShape: z.ZodType<unknown> = z
  .lazy(() =>
    z.object({
      type: JsonType,
      description: z.string().min(1).optional(),
      enum: z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
      format: ContractFormat.optional(),
      /** De valuta, alleen bij een bedrag (`format: amount`); zie {@link ContractCurrency}. */
      valuta: ContractCurrency.optional(),
      required: z.array(z.string()).optional(),
      properties: z.record(z.string().min(1), ContractProperty).optional(),
      items: ContractShape.optional(),
    }).superRefine(amountProblems),
  )
  .meta({ id: 'ContractVorm' })

/**
 * Een label zoals het processysteem het bij een veld of een optie van het dossier-contract meegeeft
 * (`koppelvlak.md` hoofdstuk 2). Elke taal mag ontbreken: het processysteem kent niet altijd beide, en
 * een veld dat in de configurator wordt geplaatst begint dan in die taal leeg, waarna de vertaalstatus
 * het meldt (R-145, D-13). Wat er staat is niet leeg.
 */
export const ContractLabel = z.strictObject({
  nl: z.string().regex(/\S/, 'een label is niet leeg').optional(),
  en: z.string().regex(/\S/, 'een label is niet leeg').optional(),
})
export type ContractLabel = z.infer<typeof ContractLabel>

/**
 * Eén veld met een naam in een contract. `description` is hier verplicht, want
 * dat is de betekenis die D-37 eist: zonder betekenis is een pad niet te kiezen
 * door iemand die het bouwblok niet heeft gebouwd, en dat is precies wat de
 * catalogus van verwijsbare velden moet doen (D-38, R-59).
 *
 * Zie `ContractVorm` hierboven voor waarom `.meta({ id })` op de export zelf staat.
 */
export const ContractProperty: z.ZodType<unknown> = z
  .lazy(() =>
    z.object({
      type: JsonType,
      description: z.string().min(1),
      /** De rol in de groepering van een reeks (D-73); zie {@link ContractRole}. */
      rol: ContractRole.optional(),
      enum: z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
      format: ContractFormat.optional(),
      /** De valuta, alleen bij een bedrag (`format: amount`); zie {@link ContractCurrency}. */
      valuta: ContractCurrency.optional(),
      required: z.array(z.string()).optional(),
      properties: z.record(z.string().min(1), ContractProperty).optional(),
      items: ContractShape.optional(),
      /**
       * Het type van de kolom in Dataverse, bij een veld van het dossier-contract (`KOLOMTYPE` in
       * `kolomtype.ts`). Hier een tekst en geen opsomming: de registry toetst het dossier-contract en
       * weigert een kolomtype dat niet wordt ondersteund met de naam van het veld erbij, en een
       * opsomming hier zou dat een melding zonder die naam maken.
       */
      kolomtype: z.string().min(1).optional(),
      /** Het label van een veld van het dossier-contract, per taal; zie {@link ContractLabel}. */
      label: ContractLabel.optional(),
      /** Bij een keuze in het dossier-contract: het label per sleutel uit `enum`. */
      optielabels: z.record(z.string().min(1), ContractLabel).optional(),
    }).superRefine(amountProblems),
  )
  .meta({ id: 'ContractEigenschap' })

/**
 * Een compartiment: de plek in het contract waar de velden landen die de inrichter zelf aan
 * het formulier heeft toegevoegd (D-69, D-70, ontwerp 8.11 en 9.5).
 *
 * Waarom dit bestaat: het gestructureerde deel van een contract staat vast voordat het
 * formulier wordt gemaakt, en zodra de inrichter zelf velden toevoegt is dat niet meer
 * waar. Zonder die splitsing valt er aan een vrij veld geen logica te verbinden over de
 * bouwblokgrens: je kunt niet afdwingen dat het is ingevuld en er geen procesafslag op
 * baseren.
 *
 * **`geeftDoor` staat op het compartiment en niet op het veld**, want dat is precies de eigenschap
 * die van het bouwblok is en niet van het formulier (D-70). Bij een aanvraagbouwblok bijvoorbeeld
 * een compartiment dat naar het beoordelen gaat en een compartiment dat daar niet komt. Daarmee is
 * het contract ook een filter: wat het ontvangende blok niet nodig heeft, gaat er niet in.
 *
 * **Een compartiment is een filter en geen afscherming van de data.** Wie een aanvrager
 * toezegt dat informatie niet verder gaat, maakt dat met een compartiment niet waar; het
 * bepaalt alleen wat een volgend bouwblok in zijn contract ziet. Het is ook niet
 * hetzelfde als de markering extern-zichtbaar per veld (R-170, D-54): twee markeringen
 * per veld, twee betekenissen, en ze mogen niet tot één instelling worden
 * samengetrokken.
 *
 * De velden in een compartiment krijgen dezelfde beschrijving per veld als het gestructureerde
 * deel, dus D-37 verandert niet van vorm; hij wordt alleen later gevuld, bij publicatie
 * in plaats van bij het inrichten van de ronde. Dat is ook waarom een formulier dat
 * hiernaar verwijst pas te bewerken is nadat dit formulier is gepubliceerd (D-71).
 */
export const Compartiment = z.object({
  id: CompartimentId,
  /** Het label komt uit het contract; het model kent alleen de identiteit (ontwerp 8.11). */
  label: Text,
  geeftDoor: z.boolean(),
  velden: z.object({
    type: z.literal('object'),
    required: z.array(z.string()).optional(),
    /**
     * Leeg bij een bouwblok waarvan nog geen formulier is gepubliceerd, en dat is een
     * geldige stand en geen gat: het compartiment bestaat in de bouwblokdefinitie voordat er
     * één veld in zit.
     */
    properties: z.record(z.string().min(1), ContractProperty),
  }),
})
export type Compartiment = z.infer<typeof Compartiment>

export const Contract = z.object({
  $schema: z.literal('https://json-schema.org/draft/2020-12/schema'),
  /**
   * De identiteit van het contract, met de ronde erin:
   * `grantify:contract:toets-aanvraag:2026-1` (ontwerp 9.5). Een contract van een
   * blok in een gepubliceerde ronde wijzigt niet (D-43), dus de ronde hoort in
   * het id en niet in een los versieveld.
   */
  $id: z.string().regex(new RegExp(`^grantify:contract:[a-z0-9-]+:${RONDE_PATROON}$`), 'verwacht grantify:contract:<blok>:<ronde>'),
  title: z.string().min(1),
  description: z.string().min(1),
  type: z.literal('object'),
  required: z.array(z.string()).optional(),
  properties: z.record(z.string().min(1), ContractProperty),
  /**
   * De compartimenten van dit bouwblok, naast het gestructureerde deel.
   *
   * Optioneel, want een volledig gestructureerd contract heeft er geen: dan stond elk
   * veld er al voordat het formulier werd gemaakt. Er is geen standaardcompartiment, ook niet
   * hier: een blok dat vrije velden toestaat noemt zijn compartimenten expliciet.
   *
   * Ze staan naast `properties` en niet in een eigen `levert`-wrapper. Een contract is
   * een JSON Schema-document (ontwerp 9.1) en dat blijft het zo; het voorbeeld in 9.5
   * schreef hier `levert` waar `properties` staat, en dat is met deze opdracht
   * rechtgezet in het document.
   */
  compartimenten: z.array(Compartiment).optional(),
})
  .superRefine((contract, ctx) => {
    /**
     * Het gestructureerde deel gebruikt geen gereserveerde sleutel (D-37, D-69, D-72).
     *
     * De toets staat op de wortel van `properties` en niet dieper, want daar zit de botsing:
     * `compartimenten` en `$stand` zijn de enige gereserveerde sleutels op de wortel van een
     * blokdocument (ontwerp 9.5), dus een eigenschap `$stand` binnen `aanvraag` landt op
     * `aanvraag.$stand` en botst met niets. Verder verbieden zou geldige contracten weigeren,
     * en dat is precies wat de kop van dit bestand niet wil.
     */
    RESERVED_KEYS.forEach((key) => {
      if (Object.hasOwn(contract.properties, key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['properties', key],
          message: `${key} is een gereserveerde sleutel op de wortel van een blokdocument`,
        })
      }
    })

    const seen = new Set<string>()
    contract.compartimenten?.forEach((compartiment, i) => {
      if (seen.has(compartiment.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['compartimenten', i, 'id'],
          // Twee compartimenten met hetzelfde id maken "in welk compartiment landt dit veld"
          // onbeslisbaar, en dat is precies de keuze die D-69 verplicht stelt.
          message: `compartiment ${compartiment.id} staat er twee keer`,
        })
      }
      seen.add(compartiment.id)
    })
  })
export type Contract = z.infer<typeof Contract>

/**
 * De registry: de contracten van een ronde met hun plaats in de keten.
 *
 * De positie is wat D-38 mogelijk maakt: de catalogus van verwijsbare velden
 * zijn de paden uit de contracten van blokken die eerder in de keten komen, plus
 * die van het eigen blok voor de reeks eerdere instanties (R-59, R-62; zie
 * `referenceableBlocks` in de engine voor waarom gelijk mag). De bouwblokken van
 * een ronde staan vast voordat de formulieren worden gemaakt (D-41).
 */
export const ContractRegistry = z
  .strictObject({
    ronde: z.string().min(1),
    bouwblokken: z
      .array(
        z.strictObject({
          blok: Block,
          /** Plaats in de keten, oplopend vanaf 1. */
          positie: z.number().int().positive(),
          /**
           * Hoe het blok heet waar een mens het leest, in beide talen, zoals in de contextkop boven
           * het formulier (R-125, R-157). Optioneel in deze vorm, zodat een registry van vóór 28
           * oktober 2026 blijft gelden; de registry-index in de opslag eist hem voor elk blok
           * (`RegistryIndex`).
           */
          naam: Text.optional(),
          contract: Contract,
        }),
      )
      .min(1),
    /**
     * De velden van het processysteem op het dossier, als contract in hetzelfde profiel
     * (`koppelvlak.md` hoofdstuk 2, R-56). Aangeleverd door het processysteem; een formulierveld
     * met een `dossierveld` wijst een eigenschap hierin aan, en de publicatiepoort toetst dat. Geen
     * positie in de keten: het dossier is er voor elk blok. Optioneel, want een ronde zonder velden
     * van het processysteem op een formulier heeft er geen nodig.
     */
    dossier: Contract.optional(),
  })
  .superRefine((registry, ctx) => {
    const blocks = new Set<string>()
    const positions = new Set<number>()
    registry.bouwblokken.forEach((row, i) => {
      if (blocks.has(row.blok)) {
        ctx.addIssue({ code: 'custom', path: ['bouwblokken', i, 'blok'], message: `blok ${row.blok} staat er twee keer` })
      }
      blocks.add(row.blok)
      if (positions.has(row.positie)) {
        // Twee blokken op dezelfde plaats maken "eerder in de keten" onbeslisbaar,
        // en daarmee de catalogus van D-38 onbepaald.
        ctx.addIssue({
          code: 'custom',
          path: ['bouwblokken', i, 'positie'],
          message: `positie ${row.positie} staat er twee keer`,
        })
      }
      positions.add(row.positie)
    })

    /**
     * Elk veld van het dossier-contract heeft een ondersteund kolomtype dat bij zijn type past
     * (`dossierContractProblems`, besluit van 27 oktober 2026). Bij het inlezen en niet pas bij het
     * publiceren: een kolomtype dat een formulier niet kan dragen is een fout in wat het processysteem
     * aanlevert, en dat hoort te blijken voordat iemand er een veld van plaatst.
     */
    if (registry.dossier !== undefined) {
      const properties = registry.dossier.properties as Parameters<typeof dossierContractProblems>[0]
      for (const problem of dossierContractProblems(properties)) {
        ctx.addIssue({ code: 'custom', path: ['dossier', 'properties', problem.naam], message: problem.wat })
      }
    }
  })

export type ContractRegistry = z.infer<typeof ContractRegistry>
